import React, { useEffect, useRef } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import { Col, Empty, InputRef, List, Pagination, Row } from 'antd';
import { Button, Input, Space, Table } from 'antd';
import type { ColumnsType, ColumnType } from 'antd/es/table';
import type { FilterConfirmProps } from 'antd/es/table/interface';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

type DataIndex = keyof DataType;

export interface DataType {
  id: number;
  department_to: string;
  name_of_post_id: string;
  updated_at: string;
  chief_id: string;
  created_at: string;
  application_status: string;
}

type ApplicationTableProps = {
  fetchApplication: DataType[];
  currentPage: number;
  setCurrentPage: any;
};

function ApplicationTable({
  fetchApplication,
  currentPage,
  setCurrentPage,
}: ApplicationTableProps) {
  const navigate = useNavigate();

  const searchInput = useRef<InputRef>(null);

  const navigateToApplication = (value: number) => {
    navigate(`/recruiting/application/${value}`);
    localStorage.setItem('applicationPage', JSON.stringify(currentPage));
  };

  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: DataIndex
  ) => {
    confirm();
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
  };

  const getColumnSearchProps = (
    dataIndex: DataIndex
  ): ColumnType<DataType> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Поиск
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Очистить
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  const columnsApplication: ColumnsType<DataType> = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'id',
      width: '5%',
      ...getColumnSearchProps('id'),
      render: (value) => {
        return (
          <a
            onClick={() => {
              navigateToApplication(value);
            }}
          >
            {value}
          </a>
        );
      },
    },
    {
      title: 'Название вакансии',
      dataIndex: 'name_of_post_id',
      key: 'name_of_post_id',
      width: '15%',
      // ...getColumnSearchProps('name_of_post_id'),
      render: (value, record) => {
        return (
          <a
            onClick={() => {
              navigateToApplication(record.id);
            }}
          >
            {value}
          </a>
        );
      },
    },
    {
      // TODO
      // dataindex change to name Director
      title: 'ФИО Руководителя',
      dataIndex: 'chief_id',
      key: 'chief_id',
      width: '15%',
      ...getColumnSearchProps('chief_id'),
      render: (value, record) => {
        return (
          <a
            onClick={() => {
              navigateToApplication(record.id);
            }}
          >
            {value}
          </a>
        );
      },
    },
    {
      // TODO
      // dataindex change to name Director
      title: 'Ответственный рекрутер',
      dataIndex: 'responsible_recruiter',
      key: 'responsible_recruiter',
      width: '15%',
      // ...getColumnSearchProps('chief_id'),
      render: (value, record) => {
        return (
          <div
            style={{display: 'flex', flexDirection: 'column'}}
            onClick={() => {
              navigateToApplication(record.id);
            }}
          >
            {value.map((element, index) => {
              return <a key={index}>• {element?.full_name}</a>
            })}
          </div>
        );
      },
    },
    {
      title: 'Структурное подразделение',
      dataIndex: 'department_to',
      key: 'department_to',
      width: '15%',
      ...getColumnSearchProps('department_to'),
      render: (value, record) => {
        return (
          <a
            onClick={() => {
              navigateToApplication(record.id);
            }}
          >
            {value}
          </a>
        );
      },
    },
    {
      title: 'Статус',
      dataIndex: 'application_status',
      key: 'application_status',
      width: '10%',
      ...getColumnSearchProps('application_status'),
      render: (value, record) => {
        let textColor = '#1677ff';
        if (value === 'Открыто' || value === 'Открыта') {
          textColor = '#11AC03';
        } else if (value === 'Закрыто' || value === 'Закрыта') {
          textColor = '#EE4D53';
        } else if (value === 'Архивировано' || value === 'Архивирована') {
          textColor = '#7136bed8';  
        }

        return (
          <div
            style={{
              fontSize: '14px',
              color: `${textColor}`,
              display: 'flex',
              alignItems: 'center',
              fontWeight: '500'
            }}
            onClick={() => {
              navigateToApplication(record.id);
            }}
          >
            {value}
          </div>
        );
      },
    },
    {
      title: 'Дата/время заявки',
      dataIndex: 'created_at',
      key: 'created_at',
      width: '10%',
      ...getColumnSearchProps('created_at'),
      render: (value, record) => {
        return (
          <a
            onClick={() => {
              navigateToApplication(record.id);
            }}
          >
            {dayjs(value).utc().format('YYYY-MM-DD / HH:mm')}
          </a>
        );
      },
    },
  ];
  const paginate = (page) => {
    setCurrentPage(page);
    localStorage.removeItem('applicationPage');
  };

  useEffect(() => {
    if (localStorage.getItem('applicationPage')) {
      setCurrentPage(Number(localStorage.getItem('applicationPage')));
      localStorage.removeItem('applicationPage');
    }
  }, [localStorage.getItem('applicationPage')]);
  return (
    <>
      <Table
        columns={columnsApplication}
        // dataSource={state.fetchedApplication}
        dataSource={fetchApplication?.slice(
          (currentPage - 1) * 10,
          currentPage * 10
        )}
        locale={{
          emptyText: <Empty description="У вас нет доступа к просмотру" />,
        }}
        rowKey="id"
        scroll={{ x: 1000 }}
        style={{ fontWeight: 'initial' }}
        pagination={false}
      />
      <Row className="mt-4" justify="end">
        <Col>
          <Pagination
            current={currentPage}
            defaultPageSize={10}
            total={fetchApplication?.length}
            showSizeChanger={false}
            onChange={paginate}
            // disabled={isLoading}
          />
        </Col>
      </Row>
    </>
  );
}

export default ApplicationTable;
